/**
 * Portable announcement bar — no Horizon/@theme dependencies.
 * Drop into assets/ and load as a module from the section.
 */
class AnnouncementBarComponent extends HTMLElement {
  #current = 0;
  /** @type {number|undefined} */
  #interval = undefined;

  connectedCallback() {
    this.#bindControls();
    this.addEventListener('mouseenter', this.suspend);
    this.addEventListener('mouseleave', this.resume);
    document.addEventListener('visibilitychange', this.#handleVisibilityChange);
    this.play();
  }

  disconnectedCallback() {
    this.suspend();
    document.removeEventListener('visibilitychange', this.#handleVisibilityChange);
  }

  #bindControls() {
    this.querySelectorAll('[data-announcement-action="previous"]').forEach((btn) => {
      btn.addEventListener('click', () => this.previous());
    });
    this.querySelectorAll('[data-announcement-action="next"]').forEach((btn) => {
      btn.addEventListener('click', () => this.next());
    });
  }

  get slides() {
    return Array.from(this.querySelectorAll('[data-announcement-slide]'));
  }

  next() {
    this.current += 1;
  }

  previous() {
    this.current -= 1;
  }

  play(interval = this.autoplayInterval) {
    if (!this.autoplay) return;

    this.paused = false;
    this.suspend();

    this.#interval = window.setInterval(() => {
      if (this.matches(':hover') || document.hidden) return;
      this.next();
    }, interval);
  }

  pause() {
    this.paused = true;
    this.suspend();
  }

  get paused() {
    return this.hasAttribute('paused');
  }

  set paused(paused) {
    this.toggleAttribute('paused', paused);
  }

  suspend = () => {
    clearInterval(this.#interval);
    this.#interval = undefined;
  };

  resume = () => {
    if (!this.autoplay || this.paused) return;
    this.play();
  };

  get autoplay() {
    return Boolean(this.autoplayInterval);
  }

  get autoplayInterval() {
    const interval = this.getAttribute('autoplay');
    const value = parseInt(`${interval}`, 10);
    if (Number.isNaN(value)) return undefined;
    return value * 1000;
  }

  get current() {
    return this.#current;
  }

  set current(current) {
    const slides = this.slides;
    if (!slides.length) return;

    this.#current = current;
    let relativeIndex = current % slides.length;
    if (relativeIndex < 0) relativeIndex += slides.length;

    slides.forEach((slide, index) => {
      slide.setAttribute('aria-hidden', `${index !== relativeIndex}`);
    });
  }

  #handleVisibilityChange = () => (document.hidden ? this.pause() : this.resume());
}

if (!customElements.get('announcement-bar-component')) {
  customElements.define('announcement-bar-component', AnnouncementBarComponent);
}
